// src/pages/OrganizationsPage.jsx
// Route: /organizations — ADMIN / HEALTH_NAVIGATOR only.
//
// Corporate onboarding, step 1: the organisations (employers) Previu invites
// people on behalf of. Creating one does not create any patient — see
// OrganizationRosterPage for what an upload does and does not do.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { organizationAPI } from '../services/api';
import Icon from '../components/Icon';

export default function OrganizationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAllowed = ['ADMIN', 'HEALTH_NAVIGATOR'].includes(user?.role);

  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [name, setName]         = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!isAllowed) return;
    organizationAPI.list()
      .then(res => setOrgs(res.data))
      .catch(e => setError(e.response?.data?.message || 'Could not load organisations.'))
      .finally(() => setLoading(false));
  }, [isAllowed]);

  if (!isAllowed) {
    return (
      <AppShell breadcrumbs={[{ label: 'Organisations' }]}>
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="lock" size={20} /></div>
          <div className="empty-title">Access Required</div>
          <div className="empty-desc">You need admin or health navigator privileges to manage organisations.</div>
        </div>
      </AppShell>
    );
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    setCreating(true);
    try {
      const res = await organizationAPI.create(name.trim());
      navigate(`/organizations/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create the organisation.');
      setCreating(false);
    }
  }

  return (
    <AppShell breadcrumbs={[{ label: 'Organisations' }]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Organisations</div>
          <div className="page-subtitle">
            Employers that ask Previu to invite their people. A roster creates invitations only —
            nobody becomes a patient until they accept the consent themselves.
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <form className="card fade-in-up delay-1" onSubmit={handleCreate} style={{ marginBottom: 20 }}>
        <div className="card-title">Add an organisation</div>
        <div className="toolbar">
          <input className="form-input" placeholder="Organisation name" aria-label="Organisation name"
            maxLength={200} value={name} onChange={e => setName(e.target.value)} />
          <button type="submit" className="btn btn-primary" disabled={creating || !name.trim()}>
            {creating ? 'Adding…' : 'Add'}
          </button>
        </div>
      </form>

      {loading ? (
        <div className="skeleton-list"><div className="skeleton skeleton--row" /><div className="skeleton skeleton--row" /></div>
      ) : orgs.length === 0 ? (
        <div className="empty-state empty-state--panel">
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="people" size={20} /></div>
          <div className="empty-title">No organisations yet</div>
          <div className="empty-desc">Add the first one above, then upload its roster.</div>
        </div>
      ) : (
        <div className="card-list fade-in-up delay-1">
          {orgs.map(o => (
            <div key={o.id} className="card-row" role="button" tabIndex={0}
              onClick={() => navigate(`/organizations/${o.id}`)}
              onKeyDown={e => e.key === 'Enter' && navigate(`/organizations/${o.id}`)}>
              <div className="card-row-body">
                <div className="card-row-title">{o.name}</div>
                <div className="card-row-meta">Added {new Date(o.createdAt).toLocaleDateString()}</div>
              </div>
              <span className={`badge ${o.status === 'ACTIVE' ? 'badge-active' : 'badge-unknown'}`}>{o.status}</span>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
